import axios from '@/plugins/axios'
import { Module } from 'vuex'

// Workspace Settings > Ideal Customer: the workspace's ideal customer
// profiles (ICPs) and the industries it serves (/api/icp/*). Nothing is kept
// in state - each page loads what it shows. GETs skip the axios cache so a
// change shows straight away, and every request is `silent` (no automatic
// error toast): the pages show the API's own error text instead.

export interface IcpIndustry {
    id: number
    name: string
}

export interface IcpCompanySize {
    id: number
    name: string
    minSize: number | null
    maxSize: number | null
    label: string
}

export interface Icp {
    id: number
    title: string
    summary: string | null
    isManual: boolean
    jobTitles: string[]
    jobFunctions: string[]
    countries: string[]
    signals: { key: string; label: string }[]
    extras: string[]
    industries: IcpIndustry[]
    companySizes: IcpCompanySize[]
    createdAt: string
}

export interface IndustryServed {
    id: number
    industry: IcpIndustry | null
    status: string
    source: 'ai' | 'user'
    reason: string | null
}

/**
 * The text to show for a failed ICP request: the API's `{"error": "..."}`
 * when it sent one, otherwise `fallback`. The axios plugin rethrows HTTP
 * errors wrapped (the original is the `cause`), so both shapes are checked.
 */
export const icpErrorMessage = (e: unknown, fallback: string): string => {
    if (typeof e === 'string' && e) {
        return e
    }

    type Failed = { cause?: Failed; response?: { data?: { error?: unknown } } }
    const failed = e as Failed | null
    const error = (failed?.cause?.response ?? failed?.response)?.data?.error

    return typeof error === 'string' && error ? error : fallback
}

// `silent`: no automatic error toast (see the axios plugin); `cache: false`:
// always ask the API.
const fresh = { cache: false, silent: true }
const silent = { silent: true }

const icpModule: Module<any, any> = {
    actions: {
        // Customer Profiles

        async getIcpProfiles(): Promise<Icp[]> {
            const { data } = await axios.get('/api/icp/profiles', fresh)
            return data?.items ?? []
        },

        async addIcpProfile(_, description: string): Promise<Icp> {
            const { data } = await axios.post('/api/icp/profiles', { description }, silent)
            return data?.item
        },

        async updateIcpProfile(_, { id, changes }: { id: number; changes: string }): Promise<Icp> {
            const { data } = await axios.put(`/api/icp/profiles/${id}`, { changes }, silent)
            return data?.item
        },

        async deleteIcpProfile(_, id: number) {
            await axios.delete(`/api/icp/profiles/${id}`, silent)
        },

        async regenerateIcpProfiles(): Promise<{ added: number; items: Icp[] }> {
            const { data } = await axios.post('/api/icp/profiles/regenerate', {}, silent)
            return data
        },

        // Industries Served

        async getIndustriesServed(): Promise<{ served: IndustryServed[]; removed: IndustryServed[] }> {
            const { data } = await axios.get('/api/icp/industries', fresh)
            return { served: data?.served ?? [], removed: data?.removed ?? [] }
        },

        async searchIcpIndustries(_, q: string): Promise<IcpIndustry[]> {
            const { data } = await axios.get('/api/icp/industries/search', { ...fresh, params: { q } })
            return data?.items ?? []
        },

        async addIndustryServed(_, industryId: number): Promise<IndustryServed> {
            const { data } = await axios.post('/api/icp/industries', { industryId }, silent)
            return data?.item
        },

        async explainIndustryServed(_, id: number): Promise<IndustryServed> {
            const { data } = await axios.post(`/api/icp/industries/${id}/explain`, {}, silent)
            return data?.item
        },

        async setIndustryServedStatus(_, { id, status }: { id: number; status: 'approved' | 'rejected' }): Promise<IndustryServed> {
            const { data } = await axios.put(`/api/icp/industries/${id}`, { status }, silent)
            return data?.item
        },

        // `picked` is the number of industries picked (or the picks themselves).
        async generateIndustriesServed(): Promise<{ picked: number | unknown[] }> {
            const { data } = await axios.post('/api/icp/industries/generate', {}, silent)
            return data
        },
    },
}

export default icpModule
